import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CreateGalleryResult,
  GalleryCategoryFilterOption,
  GalleryListItem,
  GalleryPage,
  GalleryStatus,
  GalleryTrashItem,
} from '../entities/galleries.models';
// The port owns the read params — this file only turns them into a query string. A second copy of
// the shape here was a silent way for a new param (`sort`) to be dropped on the way to the wire.
import type { GalleryOpFailure, GalleryPageParams } from './galleries.data';

// gallery-service cabinet DTOs (GalleryManageListItemDto / CreateGalleryResultDto).
// The only file that knows both the endpoint URLs and the wire shape.
interface WireListItem {
  id: string;
  title: string;
  status: string;
  shootDate: string | null;
  createdAt: string;
  coverThumbnailUrl: string | null;
  photoCount: number;
  categoryIds: string[];
  views: number;
  uniqueSessions: number;
  sizeBytes: number;
  urlSlug: string | null;
}

interface WireCreated {
  galleryId: string;
  title: string;
  urlSlug: string;
  publicUrl: string;
  presetWarnings?: string[];
  guestPasscodeSet?: boolean;
}

interface WirePage<T> {
  items: T[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
}

function pageParams(params?: GalleryPageParams): HttpParams {
  let hp = new HttpParams();
  if (params?.cursor) hp = hp.set('cursor', params.cursor);
  if (params?.perPage) hp = hp.set('perPage', String(params.perPage));
  // `categoryId` repeats once per selected category — the server reads one or many.
  for (const id of params?.categoryId ? [params.categoryId].flat() : []) hp = hp.append('categoryId', id);
  if (params?.q?.trim()) hp = hp.set('q', params.q.trim());
  // Absent = the route's own default order; the trash relies on that for «удалятся раньше — выше».
  if (params?.sort) hp = hp.set('sort', params.sort);
  return hp;
}

@Injectable({ providedIn: 'root' })
export class GalleriesApiService {
  private readonly http = inject(HttpClient);

  async getList(params?: GalleryPageParams): Promise<GalleryPage<GalleryListItem> | null> {
    try {
      const wire = await firstValueFrom(this.http.get<WirePage<WireListItem>>('/api/gallery', { params: pageParams(params) }));
      return { items: wire.items.map(toListItem), total: wire.total, hasMore: wire.hasMore, nextCursor: wire.nextCursor };
    } catch {
      // 401 is handled by the error interceptor (redirect to identity); anything else = load failure.
      return null;
    }
  }

  async create(title: string, presetId?: string | null): Promise<CreateGalleryResult> {
    try {
      // With a preset the create routes through gallery-presets, which seeds
      // the new gallery from the preset bundle (one-shot copy, no live link).
      const wire = await firstValueFrom(
        presetId
          ? this.http.post<WireCreated>('/api/gallery-presets/create-gallery', { title, presetId })
          : this.http.post<WireCreated>('/api/gallery', { title }),
      );
      const { presetWarnings, guestPasscodeSet, ...gallery } = wire;
      return { ok: true, gallery, presetWarnings, guestPasscodeSet };
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 400) return { ok: false, reason: 'invalid' };
      return { ok: false, reason: 'error' };
    }
  }

  // Spec 01 «Templates & copies»: server clones the gallery into a fresh empty
  // one and returns the new gallery id (null on 404/failure).
  async duplicate(galleryId: string): Promise<string | null> {
    try {
      const wire = await firstValueFrom(this.http.post<WireCreated>(`/api/gallery/${galleryId}/duplicate`, {}));
      return wire.galleryId;
    } catch {
      return null;
    }
  }

  async rename(galleryId: string, title: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.put(`/api/gallery/${galleryId}/title`, { title }));
      return true;
    } catch {
      return false;
    }
  }

  async delete(galleryId: string): Promise<GalleryOpFailure | null> {
    try {
      await firstValueFrom(this.http.delete(`/api/gallery/${galleryId}`));
      return null;
    } catch (err) {
      return toOpFailure(err);
    }
  }

  // The per-gallery category DIFF (assign/remove/createNames) the manage modal already writes with —
  // a batch is this call once per gallery, so no bulk route is minted. Failure statuses come back as
  // 403/404 from the controller's `respond()`, which is what the reason column reads.
  async applyCategories(
    galleryId: string,
    add: readonly string[],
    remove: readonly string[],
    createNames: readonly string[] = [],
  ): Promise<GalleryOpFailure | null> {
    try {
      await firstValueFrom(
        this.http.post(`/api/gallery-categories/galleries/${galleryId}/apply`, {
          assign: [...add],
          remove: [...remove],
          createNames: [...createNames],
        }),
      );
      return null;
    } catch (err) {
      return toOpFailure(err);
    }
  }

  // Same endpoint the per-gallery settings page uses; the list needs it for bulk publish/hide.
  // The reason travels: the status dialog says «Нет доступа» on a 403 and the generic line otherwise.
  async setStatus(galleryId: string, status: GalleryStatus): Promise<GalleryOpFailure | null> {
    try {
      await firstValueFrom(this.http.put(`/api/gallery/${galleryId}/status`, { status }));
      return null;
    } catch (err) {
      return toOpFailure(err);
    }
  }

  async getTrash(params?: GalleryPageParams): Promise<GalleryPage<GalleryTrashItem> | null> {
    try {
      return await firstValueFrom(
        this.http.get<GalleryPage<GalleryTrashItem>>('/api/gallery/trash', { params: pageParams(params) }),
      );
    } catch {
      return null;
    }
  }

  async restore(galleryId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post(`/api/gallery/${galleryId}/restore`, {}));
      return true;
    } catch {
      return false;
    }
  }

  async purge(galleryId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.delete(`/api/gallery/${galleryId}/purge`));
      return true;
    } catch {
      return false;
    }
  }

  async emptyTrash(): Promise<boolean> {
    try {
      await firstValueFrom(this.http.delete('/api/gallery/trash'));
      return true;
    } catch {
      return false;
    }
  }

  async categoryVocabulary(): Promise<GalleryCategoryFilterOption[] | null> {
    try {
      return await firstValueFrom(this.http.get<GalleryCategoryFilterOption[]>('/api/gallery-categories'));
    } catch {
      return null;
    }
  }
}

// 403 = the gallery is not this photographer's to touch, 404 = it is already gone, 422 = the
// category apply route refused on its own per-gallery ceiling (`max_exceeded`, gallery-categories
// service); anything else (network, 5xx, a malformed body) reads as a plain error.
function toOpFailure(err: unknown): GalleryOpFailure {
  if (err instanceof HttpErrorResponse) {
    if (err.status === 403) return 'no-access';
    if (err.status === 404) return 'missing';
    if (err.status === 422) return 'too-many';
  }

  return 'error';
}

function toStatus(status: string): GalleryStatus {
  return status === 'hidden' ? 'hidden' : 'published';
}

function toListItem(w: WireListItem): GalleryListItem {
  return {
    id: w.id,
    title: w.title,
    status: toStatus(w.status),
    shootDate: w.shootDate,
    // Raw pg timestamp strings pass through verbatim; the card formatter parses them.
    createdAt: w.createdAt,
    coverThumbnailUrl: w.coverThumbnailUrl,
    photoCount: w.photoCount,
    categoryIds: w.categoryIds ?? [],
    views: w.views,
    uniqueSessions: w.uniqueSessions,
    sizeBytes: w.sizeBytes,
    urlSlug: w.urlSlug,
  };
}
